import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';

import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import { Icon } from '../common/Icon';
import { IconButton } from '../common/IconButton';
import { fromNow, prettyDuration } from '../common/TimeUtils';
import NotesEditor from '../common/editors/NotesEditor';
import AIResponseStats from '../ai/AIResponseStats';
import AIModelSelector from '../../pages/cerokm/components/AIModelSelector';
import { UserAvatarLive } from '../common/UserAvatarLive';
import CountryFlag from '../common/CountryFlag';
import useUser from '../common/react-hooks/useUser';
import { AttachButton, AttachmentChips, useEmailAttachments } from '../common/EmailAttachments';
import __ from '../../../../components/interop/i18n';

// A premium user's request (model/premiumSupportTasks.js), answered where the user asked
const KIND_LABELS = {
  feedback: 'Feedback', dataFeedback: 'Data feedback', email: 'Email', report: 'Report', comment: 'Comment',
};

const Label = ({ children, className = '' }) => (
  <div className={`text-secondary text-uppercase small ${className}`}>{children}</div>
);

const headline = (input) => _.truncate(input?.topic || '', { length: 140 });

// From each time the user wrote until our next reply, added up: across reopenings and replies
function waitedForUs(events = []) {
  let total = 0;
  let waitingSince = null;
  for (const e of events) {
    if (e.type === 'userMessage') {
      waitingSince = waitingSince || new Date(e.at);
    } else if (waitingSince) {
      total += new Date(e.at) - waitingSince;
      waitingSince = null;
    }
  }
  return total;
}

function Status({ task }) {
  const { input: { support = {} } = {}, output = {}, finishedAt, createdAt } = task;
  const firstResponse = support.firstResponseAt
    ? <>first response in {prettyDuration(new Date(support.firstResponseAt) - new Date(createdAt))} by {support.firstResponseBy}</>
    : `no response yet, ${fromNow(createdAt)}`;
  return <div className={'mb-3'}>
    {finishedAt
      ? <span className={'badge badge-success mr-2'}>Resolved{output.resolution === 'noReply' ? ` without reply by ${output.by}` : `: replied by ${output.by}`}</span>
      : <span className={'badge badge-warning mr-2'}>Open</span>}
    <span className={'small text-secondary'}>
      {new Date(createdAt).toLocaleString()} · {firstResponse}
      {finishedAt && <> · waited for us {prettyDuration(waitedForUs(support.events))} in all</>}
      {support.reopenCount ? ` · reopened ${support.reopenCount}×` : ''}
    </span>
  </div>;
}

// The account and the casual user it bought as (Aleph's filter is a regex)
const alephUrl = (ids) => `https://metrics2.rewise.ai/ui/live/aleph#${encodeURIComponent(JSON.stringify({ searchLive: false, maxResults: 10, archiveSearchSpan: 'day', filter: _.uniq(_.compact(ids)).join('|') }))}`;
const PLAN_TAG_CLASSES = { yearly: 'TagLink--gold', monthly: 'TagLink--silver' };

function UserLine({ user = {} }) {
  const info = useUser(user.userId);
  const name = user.userName || user.userId;
  return <div className={'mb-3 d-flex align-items-center flex-wrap'}>
    <IconButton icon={'search'} className={'mr-2'} title={'Explore the user in Aleph'} href={alephUrl([user.userId, info?.casualUserId])} target={'_blank'}/>
    {info?.pais && <CountryFlag countryCode={info.pais} className={'mr-1'}/>}
    <UserAvatarLive id={user.userId} user={{ id: user.userId, username: name }}/>
    {user.userAdminUrl
      ? <a className={'ml-1'} href={user.userAdminUrl} target={'_blank'} rel={'noreferrer'}>{name}</a>
      : <span className={'ml-1'}>{name}</span>}
    {user.realName && <span className={'small text-secondary ml-2'} title={'As typed on the payment page'}>(name on card: {user.realName})</span>}
    {user.plan && <span className={`ml-2 ${PLAN_TAG_CLASSES[user.plan] || 'badge badge-primary'}`}>{user.plan}</span>}
    <span className={'small ml-2'}>{user.userEmail || <span className={'text-danger'}>no email</span>}</span>
    {user.vehicles?.length > 0 && <span className={'small text-secondary ml-2'}>🚗 {user.vehicles.join(' · ')}</span>}
  </div>;
}

function Conversation({ events = [] }) {
  return <div className={'mb-3'}>
    {events.map((e, i) => {
      const ours = e.type !== 'userMessage';
      return <div key={i} className={`d-flex mb-2 ${ours ? 'justify-content-end' : ''}`}>
        <div className={`rounded p-2 ${ours ? 'bg-light-success' : 'bg-light'}`} style={{ maxWidth: '75%' }}>
          <div className={'small text-secondary'}>
            {ours ? `${e.by}${e.type === 'staffComment' ? ' (comment on the site)' : ''}` : 'User'} · {new Date(e.at).toLocaleString()}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{e.text || <i className={'text-secondary'}>No message</i>}</div>
          {_.map(e.attachments, (a) => <div key={a.url} className={'small mt-1'}>
            <Icon small icon={'attach_file'}/> <a href={a.url} target={'_blank'} rel={'noreferrer'}>{a.fileName}</a>
          </div>)}
        </div>
      </div>;
    })}
  </div>;
}

function Details({ details = [], links = [] }) {
  const [open, setOpen] = useState(true);
  return <div className={'mb-3'}>
    <div className={'cursor-pointer text-secondary small'} onClick={() => setOpen((o) => !o)}>
      <Icon small icon={open ? 'expand_less' : 'expand_more'}/> DETAILS AND LINKS
    </div>
    {open && <div className={'mt-1'}>
      <table className={'table table-sm small mb-2'}>
        <tbody>{details.map((d, i) => <tr key={i}><td className={'text-secondary'}>{d.label}</td><td>{d.value}</td></tr>)}</tbody>
      </table>
      {links.map((l) => <a key={l.url} href={l.url} target={'_blank'} rel={'noreferrer'} className={'mr-3 small'}>{l.label}</a>)}
    </div>}
  </div>;
}

function OtherRequests({ task }) {
  const { page } = useContext(LegoAdminPageContext);
  const [others, setOthers] = useState(null);
  const userId = task.input?.user?.userId;

  useEffect(() => {
    page.runAsync(async () => {
      const tasks = await page.service('services/data-tasks').find({
        query: { typeId: task.typeId, 'input.user.userId': userId, $sort: { createdAt: -1 }, $limit: 20, $select: ['input.kind', 'input.topic', 'createdAt', 'finishedAt'] },
      });
      setOthers(_.reject(tasks, { _id: task._id }));
    });
  }, [task._id]);

  if (!others?.length) {
    return null;
  }
  return <div className={'mb-3'}>
    <Label className={'mb-1'}>Other requests of this user</Label>
    {others.map((t) => <div key={t._id} className={'small'}>
      <Icon small icon={t.finishedAt ? 'done' : 'schedule'} level={t.finishedAt ? 'success' : 'warning'}/>
      <span className={'text-secondary ml-1'}>{KIND_LABELS[t.input?.kind]}:</span>
      <a href={`/tasks/batches/ids:${t._id}`} target={'_blank'} rel={'noreferrer'} className={'ml-1'}>{headline(t.input)}</a>
      <span className={'text-secondary'}> · {new Date(t.createdAt).toLocaleDateString()}</span>
    </div>)}
  </div>;
}

// The AI draft (model/premiumSupportDrafts.js), shown and redrafted in each answer pane
function useDraft(task) {
  const { draft, drafts } = task.input?.support || {};
  const [stored, setStored] = useState({ draft, drafts });
  useEffect(() => setStored({ draft, drafts }), [draft?.at]);
  return [stored, setStored];
}

function DraftInfo({ drafting: { draft, drafts } }) {
  const draftsCost = _.sumBy(drafts, 'stats.costEstimate');
  return <div className={'mb-1'}>
    <div className={'small text-secondary'}>
      AI draft · {draft ? fromNow(draft.at) : 'none yet'}
      {draftsCost > 0 && ` · all drafts $${draftsCost.toFixed(4)}`}
    </div>
    {draft && <AIResponseStats stats={{ ...draft.stats, modelName: draft.model }}/>}
    {draft?.notes && <div className={'small border-left border-warning bg-white pl-2 py-1 mt-1'} style={{ whiteSpace: 'pre-wrap' }}>
      <Icon small icon={'sticky_note_2'} level={'warning'}/> <strong>Note:</strong> {draft.notes}
    </div>}
  </div>;
}

const FOUND_IT = 'We found what they asked for and just added it to the site: link it';
const CLEAN_UP = 'Fix the language only, so it reads as written by a native speaker: natural and plain. Keep the content, facts, links and tone';

// Keep in sync with support-playbook/research/manuals.md's appliesTo
const isResearchable = ({ kind, refs = {} }) => kind === 'dataFeedback'
  || (kind === 'feedback' && ['repairManualDataRequest', 'manualRequest', 'fusebox', 'wiringDiagrams', 'premium'].includes(refs.modalType));

// `current`: the reply as edited, which redrafts revise
function DraftControls({ drafting: { task, draft, onDraft }, channel, current }) {
  const { page } = useContext(LegoAdminPageContext);
  const service = page.service('services/data/premium-support-drafts');
  const [instruction, setInstruction] = useState('');
  const [model, setModel] = useState(null);
  // Which prompt was just copied
  const [copied, setCopied] = useState(null);

  const redraft = (instructionToUse = instruction) => page.runAsync(async () => {
    const updated = await service.create({ taskId: task._id, instruction: instructionToUse.trim() || undefined, current, model, channel });
    onDraft(_.pick(updated.input.support, ['draft', 'drafts']));
    setInstruction('');
  }, 'Drafting...');

  const copyPrompt = (which) => page.runAsync(async () => {
    const { prompt } = await service.get(task._id, { query: { prompt: which, channel } });
    await navigator.clipboard.writeText(prompt);
    setCopied(which);
    setTimeout(() => setCopied(null), 2000);
  });

  return <div className={'border rounded p-2 mb-3'}>
    <input className={'form-control form-control-sm mb-2'} placeholder={draft ? 'What to change (e.g. shorter)' : 'What to say (optional)'}
           value={instruction} onChange={(e) => setInstruction(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && redraft()}/>
    <div className={'d-flex align-items-center mb-1'}>
      <span className={'flex-grow-1 mr-2'}><AIModelSelector value={model} onChange={setModel}/></span>
      <IconButton icon={'spellcheck'} level={'secondary'} disabled={!current?.trim()} className={'mr-1'}
                  onClick={() => redraft(_.compact([CLEAN_UP, instruction.trim()]).join('. Also: '))}
                  title={'Fixes the English of the text as edited, plus what to change if typed'}>Clean up</IconButton>
      <IconButton icon={'auto_awesome'} level={'primary'} onClick={() => redraft()}>{draft ? 'Redraft' : 'Draft'}</IconButton>
    </div>
    {['report', 'comment'].includes(task.input.kind)
      && <IconButton icon={copied === 'reply' ? 'done' : 'content_copy'} level={'secondary'} onClick={() => copyPrompt('reply')}
                     title={'To write the reply with another LLM'}>{copied === 'reply' ? 'Copied' : 'Copy reply prompt'}</IconButton>}
    {isResearchable(task.input) && <>
      <IconButton icon={copied === 'research' ? 'done' : 'travel_explore'} level={'secondary'} onClick={() => copyPrompt('research')}
                  title={'To research the request with an agent'}>{copied === 'research' ? 'Copied' : 'Copy research prompt'}</IconButton>
      <IconButton icon={'task_alt'} level={'secondary'} onClick={() => redraft(FOUND_IT)}
                  title={'Once what the research found is on the site: drafts the reply linking it'}>Found it draft</IconButton>
    </>}
  </div>;
}

// A new email quotes what the user wrote, as email clients and our other feedback replies (ModerateFeedback) do
function quoteOf(task) {
  const message = _.findLast(task.input.support?.events, { type: 'userMessage' });
  if (!message?.text || task.input.support.lastInboundMsg) {
    return '';
  }
  const date = new Date(message.at).toLocaleString(undefined, { weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' });
  const header = __('emails.feedbackReply.quotedHeader', { date, userName: task.input.user?.userName });
  return `${header}\n${message.text.trim().replace(/^/gm, '\t')}`;
}

const withoutEnd = (text, end) => (end && text.endsWith(end) ? text.slice(0, -end.length).trimEnd() : text);

// Who signs the emails we send
const SIGN_NAMES = { 'brunocuervo@gmail.com': 'Bruno', 'jsilveira@gmail.com': 'Javier', 'gonzaloar.gr@gmail.com': 'Andy' };

function EmailReply({ task, batchEditor, drafting }) {
  const { page } = useContext(LegoAdminPageContext);
  const { draft } = drafting;
  const { user = {}, refs = {} } = task.input || {};
  const inThread = task.input?.support?.lastInboundMsg;
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const { attachments, chips, busy, clear, upload, dragging, dropZoneProps } = useEmailAttachments();
  const quote = quoteOf(task);
  const signature = draft?.reply ? _.compact([draft.closing, SIGN_NAMES[window.user?.email]]).join('\n') : '';

  useEffect(() => {
    setSubject(draft?.subject || '');
    setBody(_.compact([draft?.reply?.trimEnd(), signature, quote]).join('\n\n'));
  }, [draft?.at]);

  if (!user.userEmail) {
    return <div className={'alert alert-secondary small'}>The user has no email.</div>;
  }

  const send = (keepOpen) => page.runAsync(async () => {
    const options = {
      createdBy: page.getLoggedUserSignature(),
      supportTaskId: task._id,
      supportKeepOpen: keepOpen,
      feedbackId: refs.feedbackId,
      dataFeedbackId: refs.dataFeedbackId,
      attachments,
    };
    // Plain text: what the user wrote (in the quote) or we did must not become markup
    const html = _.escape(body).replace(/\n/g, '<br>').replace(/\t/g, '&emsp;');
    const message = inThread
      ? { messageToReply: inThread, html, options }
      : { userTo: { id: user.userId, email: user.userEmail }, subject, html, options };
    const sent = await page.service('services/data/emails/conversations').create(message);
    if (!sent?.sparkpostEmailId) {
      throw new Error('The email could not be sent: the task stays as it was');
    }
    setBody('');
    clear();
    // Resolved on the server: move on
    if (!keepOpen) {
      await batchEditor.next();
    }
  });

  // Not the quote alone
  const canSend = body.replace(quote, '').trim() && (inThread || subject.trim()) && !busy;
  return <div className={'mb-4'}>
    <Label className={'mb-1'}>Reply by email to {user.userEmail}</Label>
    <DraftInfo drafting={drafting}/>
    {inThread
      ? <div className={'small text-secondary mb-1'}>In the thread "{inThread.subject}"</div>
      : <input className={'form-control form-control-sm mb-1'} placeholder={'Subject'} value={subject} onChange={(e) => setSubject(e.target.value)}/>}
    <div className={`mb-1 ${dragging ? 'border border-primary rounded' : ''}`} {...dropZoneProps}>
      <textarea className={'form-control form-control-sm'} rows={10} value={body} onChange={(e) => setBody(e.target.value)}/>
      <AttachmentChips {...chips}/>
    </div>
    <DraftControls drafting={drafting} channel={'email'} current={withoutEnd(withoutEnd(body.trimEnd(), quote), signature)}/>
    <div className={'d-flex justify-content-between'}>
      <span>
        <IconButton icon={'send'} fill level={'success'} disabled={!canSend} onClick={() => send(false)}>Send & resolve</IconButton>
        <AttachButton upload={upload}/>
      </span>
      <IconButton icon={'schedule_send'} level={'primary'} disabled={!canSend} onClick={() => send(true)}>Send & keep open</IconButton>
    </div>
  </div>;
}

// The site's reply box opens prefilled with it, for admins (views/modeloDefectoThread.pug)
const withDraft = (url, text) => (text.trim()
  ? `${url.replace('#select-com', '#reply-com')}${url.includes('#') ? '&' : '#'}reply=${encodeURIComponent(text)}`
  : url);

// Answered publicly on the site, where it helps everyone with the same problem
function AnswerOnSite({ task, batchEditor, drafting }) {
  const [emailInstead, setEmailInstead] = useState(false);
  const [text, setText] = useState('');
  useEffect(() => setText(drafting.draft?.reply || ''), [drafting.draft?.at]);
  const { links = [] } = task.input || {};
  // Their latest comment, also on a report they came back to; the report until they comment
  const toComment = links.find((l) => l.label === 'Open the comment');
  const target = toComment || links.find((l) => l.label === 'Open the report');
  if (emailInstead) {
    return <EmailReply task={task} batchEditor={batchEditor} drafting={drafting}/>;
  }
  return <div className={'mb-4'}>
    <Label className={'mb-1'}>Answer on the site</Label>
    {target && <a className={'btn btn-success mb-1'} href={withDraft(target.url, text)} target={'_blank'} rel={'noreferrer'}>
      <Icon icon={'forum'} level={'white'}/> {toComment ? 'Reply to their comment' : 'Answer the report'}
    </a>}
    <div className={'small text-secondary mb-2'}>Our comment there resolves this task.</div>
    <DraftInfo drafting={drafting}/>
    <textarea className={'form-control form-control-sm mb-1'} rows={8} value={text} onChange={(e) => setText(e.target.value)}/>
    <div className={'mb-1'}>
      <IconButton icon={'content_copy'} level={'primary'} disabled={!text.trim()} onClick={() => navigator.clipboard.writeText(text)}>Copy</IconButton>
    </div>
    <DraftControls drafting={drafting} channel={'site'} current={text}/>
    <a href={'#'} className={'small'} onClick={(e) => { e.preventDefault(); setEmailInstead(true); }}>Email the user instead</a>
  </div>;
}

// Answered through its pipeline, whose data issue emails the user (DataIssueRules): the "we found your fusebox" one resolves this task
function PipelineStep({ label, state, level, url }) {
  return <div className={'d-flex align-items-center justify-content-between mb-1'}>
    <span>{label} <span className={`badge badge-${level} ml-1`}>{state}</span></span>
    {url && <a className={'btn btn-sm btn-primary'} href={url} target={'_blank'} rel={'noreferrer'}>Open</a>}
  </div>;
}

const taskStep = (label) => (t) => {
  const done = t.finishedAt ? (_.isString(t.output) ? t.output : 'done') : 'open';
  return <PipelineStep key={t._id} label={label} state={done} level={done === 'open' ? 'warning' : done === 'rejected' ? 'danger' : 'success'} url={`/tasks/batches/ids:${t._id}`}/>;
};

function AnswerDataFeedback({ task, batchEditor, drafting }) {
  const { page } = useContext(LegoAdminPageContext);
  const [pipeline, setPipeline] = useState(null);
  const [emailInstead, setEmailInstead] = useState(false);

  useEffect(() => {
    page.runAsync(async () => setPipeline(await page.service('services/data/data-feedback-pipeline').get(task.input.refs.dataFeedbackId)));
  }, [task._id]);

  const reviews = _.filter(pipeline?.tasks, { typeId: 'imageUserUpload' });
  const clusterings = _.filter(pipeline?.tasks, { typeId: 'imgFuseboxClustering' });
  const notYet = (label) => <PipelineStep label={label} state={'not yet'} level={'secondary'}/>;
  const { reason, images = [] } = pipeline?.dataFeedback?.data || {};
  return <div className={'mb-4'}>
    <Label className={'mb-1'}>Its pipeline</Label>
    {pipeline?.dataFeedback && <div className={'small mb-1'}>
      {images.map((img) => <a key={img.url} href={img.url} target={'_blank'} rel={'noreferrer'}><img src={img.url} alt={''} height={60} className={'mr-1 rounded'}/></a>)}
      <div>Reason: <strong>{reason || '-'}</strong> · state: {pipeline.dataFeedback.state}</div>
    </div>}
    {pipeline && <div className={'border rounded p-2 mb-1 bg-white'}>
      {reviews.length ? reviews.map(taskStep('Image review')) : notYet('Image review')}
      {clusterings.length ? clusterings.map(taskStep('Clustering')) : notYet('Clustering')}
      {pipeline.issues.length
        ? pipeline.issues.map((issue) => <PipelineStep key={issue._id} label={`Data issue: ${issue.issueCategory}`} state={issue.state}
                                                       level={issue.state === 'solved' ? 'success' : 'warning'}
                                                       url={`/legos/data-issues?editing=${encodeURIComponent(JSON.stringify(issue._id))}`}/>)
        : notYet('Data issue')}
    </div>}
    <div className={'small text-secondary mb-2'}>Its data issue's emails answer the user and resolve this task.</div>
    {emailInstead
      ? <EmailReply task={task} batchEditor={batchEditor} drafting={drafting}/>
      : <a href={'#'} className={'small'} onClick={(e) => { e.preventDefault(); setEmailInstead(true); }}>Email the user</a>}
  </div>;
}

const ANSWERS = { report: AnswerOnSite, comment: AnswerOnSite, dataFeedback: AnswerDataFeedback };

function Answer({ task, batchEditor }) {
  const [{ draft, drafts }, setDraft] = useDraft(task);
  const AnswerOfKind = ANSWERS[task.input?.kind] || EmailReply;
  return <AnswerOfKind task={task} batchEditor={batchEditor} drafting={{ task, draft, drafts, onDraft: setDraft }}/>;
}

function Resolution({ task, batchEditor }) {
  const { page } = useContext(LegoAdminPageContext);

  if (task.finishedAt) {
    return <div className={'mb-4'}>
      <IconButton icon={'undo'} level={'primary'} onClick={() => batchEditor.reopenTask().catch(alert)}>Reopen</IconButton>
    </div>;
  }
  const resolveWithoutReply = () => batchEditor.doneAndNext({ resolution: 'noReply', by: page.getLoggedUserSignature().username }).catch(alert);
  return <div className={'mb-4'}>
    <IconButton icon={'done'} level={'secondary'} onClick={resolveWithoutReply}>Resolve without reply</IconButton>
  </div>;
}

export default function TaskEditorPremiumSupport({ batchEditor, task }) {
  const { kind, details, links, user } = task.input || {};

  return <div className={'grid-pdf-extraction'}>
    <div className={'bg-white overflow-auto p-3'}>
      <h5><span className={'badge badge-dark mr-2'}>{KIND_LABELS[kind] || kind}</span>{headline(task.input)}</h5>
      <Status task={task}/>
      <UserLine user={user}/>
      <Conversation events={task.input?.support?.events}/>
      <Details details={details} links={links}/>
      <OtherRequests task={task}/>
    </div>

    <div className={'p-3 bg-light overflow-auto'}>
      <Answer task={task} batchEditor={batchEditor} key={task._id}/>
      <Resolution task={task} batchEditor={batchEditor}/>
      <Label className={'mb-1'}>Notes</Label>
      <NotesEditor value={task.notes} onChange={(notes) => batchEditor.updateNotes(notes).catch(alert)}/>
    </div>
  </div>;
}
